<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import WomanInstallingSoundproofInsertsImage from './images/ashley_installing_indow_insert.jpg';
	import ConstructionWorkerDoingRoadConstructionImage from './images/construction_worker_doing_road_construction.jpg';
	import HomeownerInstallingWindowInsertImage from './images/homeowner_installing_window_insert.jpg';
	import HowNoiseInfiltratesBuildingsImage from './images/how_to_prevent_noise_pollution.jpg';
</script>

<Section>
	<Image
		alt="Construction worker doing road construction"
		src={ConstructionWorkerDoingRoadConstructionImage}
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<p>
				<a
					href="https://pixabay.com/photos/road-construction-192894"
					target="_blank"
					rel="external">Source</a
				>
			</p>
		{/snippet}
	</Image>
	<p>
		In the 21st century, people everywhere face pollution on a daily basis. And
		of all the different forms of pollution, noise pollution is both overlooked
		and damaging, making it an unnoticed threat.
	</p>
	<p>
		The health dangers of noise pollution are many and, though rarely fatal, can
		lead to extreme discomfort and deafness. Fortunately, there are plenty of
		ways to combat noise pollution and its harmful effects.
	</p>
</Section>

<Section>
	<h2>So, What Is Noise Pollution?</h2>
	<p>
		<a
			href="https://www.epa.gov/clean-air-act-overview/clean-air-act-title-iv-noise-pollution#:~:text=What%20is%20Noise,fact%20that%20you"
			target="_blank"
			rel="external">According to the EPA</a
		>, noise is usually defined as “unwanted or disturbing sound.” Noise
		pollution is noise that intrudes into a place where the sound is unwanted
		and harmful. And noise pollution is more than annoying — it’s dangerous. In
		order to better understand that danger, we use
		<a href="/resources/faq/interior-storm-window-inserts-glossary#acoustic"
			>decibels</a
		> to measure the intensity and loudness of sound.
	</p>
	<p>
		The level of decibels that cause noise pollution effects on humans is
		relatively low. For instance, anything louder than 85 decibels can harm your
		ears and cause long-lasting hearing damage. Something as mundane as city
		traffic, or even a lawnmower, produces decibels in the 80-90 range.
	</p>
	<Image
		alt="Graphic showing various sound frequencies & how they infiltrate: insulate against noise pollution"
		src={HowNoiseInfiltratesBuildingsImage}
		class="w-full"
	/>
	<p>
		So, we know what constitutes noise pollution, but how does noise pollution
		affect humans?
	</p>
	<p>
		The way we perceive sound is also the way we are harmed by sound.
		Fundamentally, sound is vibrations in the air. These vibrations are “read”
		by tiny hair cells in your ears which transmit the vibrations to your brain.
		When those vibrations are too big (i.e. high decibels), they can damage
		those hair cells permanently. They can’t be repaired, and they don’t regrow.
		This sort of damage is known as <a
			href="https://www.nidcd.nih.gov/health/noise-induced-hearing-loss"
			target="_blank"
			rel="external">Noise-Induced Hearing Loss (NIHL)</a
		>.
	</p>
	<p>
		Besides NIHL, noise pollution can cause high blood pressure and subsequent
		heart problems. The high-decibel signals that get sent to the brain can
		stimulate the brain in such a way that it releases adrenaline which can
		over-excite your heart and cause stress.
	</p>
	<p>
		Children are especially susceptible to noise pollution. Studies have shown
		that noise pollution leads to impaired memory, attention, and reading skill.
	</p>
	<p>
		Now that we know how noise pollution affects human health, we can learn how
		to combat it and prevent it.
	</p>
</Section>

<Section>
	<h2>What You Can Do About It</h2>
	<Image
		alt="woman installing Indow window inserts to reduce noise next to bookshelves"
		src={WomanInstallingSoundproofInsertsImage}
		class="w-full"
	/>
	<p>
		Noise pollution poses an obvious threat. The trick with fighting hearing
		loss is not to treat damage, but to prevent it.
	</p>
	<p>
		Protecting your hearing can be as quick as slipping in a pair of earplugs
		when mowing the lawn or when you’re using loud products to clean your home.
		However, there are noises out of your control that can infiltrate your
		house. Plus, it’s not really that fun to wear earplugs all day long in your
		own home. The solution? Get earplugs for your house itself.
	</p>
	<p>
		With all the everyday noises, it can be hard to actually tell if those
		sounds are putting your hearing in danger. Most people don’t have a decibel
		meter on hand to see if the traffic outside has been damaging their ears and
		hearing.
	</p>
	<p>
		In short, it’s better to be safe than sorry. If you find yourself feeling
		stressed or annoyed from the nearby airport, the train that passes right by
		your backyard, or the hum of the interstate fifty yards away, then you are
		suffering from noise pollution.
	</p>
	<p>
		The good news is that you’ve done the first step to solving the problem:
		identifying it.
	</p>
	<p>
		If the noise pollution source is external to your home, you always have the
		option of <a href="/resources/blog/how-to-file-noise-complaint-online"
			>filing a noise complaint</a
		>, but that’s a short-term fix for a long-term problem. Before you set about
		combating noise pollution, it’s important to identify the source. Is the
		pollution coming from within the house or outside of it? The source of the
		sound will determine whether or not you need to use sound-absorbing or
		soundproofing methods.
	</p>
</Section>

<Section>
	<h2>Sound Absorbing</h2>
	<p>
		Sound absorption deals with reducing the noise that occurs within a room or
		enclosed space. Sound waves bounce off of surfaces, and the harder and
		flatter the surface, the more the waves will bounce. Here are a few ways to
		soften up a room and increase sound absorption:
	</p>
	<div>
		<p class="font-bold">Carpets and Rugs:</p>
		<p>
			A nice rug can really tie the room together and reduce noise pollution to
			boot. They’re stylish, take almost no effort to install, and are a quick
			way to neutralize a highly reflective surface.
		</p>
	</div>
	<div>
		<p class="font-bold">Tapestries and Curtains:</p>
		<p>
			Walls and ceilings love soundwaves, that’s why auditoriums are typically
			designed to have highly concave stone surfaces to really trap that sound
			and amplify it. You can flip those properties on their heads and modify
			your walls to be convex and soft. Use <a
				href="/resources/blog/most-effective-soundproofing-materials"
			>
				tapestries, bookshelves, curtains, even industry-grade noise-canceling
				panels
			</a>, anything to reduce the hard, flat surface area that sound waves
			love.
		</p>
	</div>
</Section>

<Section>
	<h2>Soundproofing</h2>
	<Image
		alt="man installing window insert to block noise and drafts"
		src={HomeownerInstallingWindowInsertImage}
	/>
	<p>
		In most cases, noise pollution is an external problem. We live in a noisy
		world, but your house should be for peace and quiet. Soundproofing is a
		great way to keep all the annoying and damaging noise out of everything
		ranging from your <a href="/resources/blog/soundproof-home-office"
			>home office</a
		>
		to your <a href="/resources/blog/soundproof-apartment">whole apartment</a>.
		Let’s review the pros and cons of a few simple methods that will do wonders
		for your hearing.
	</p>
	<div>
		<p class="font-bold">Bookshelves:</p>
		<p>
			If you share a wall with a noisy neighbor, bookshelves are a great way to
			block out their conversations and loud music. The only prerequisite here
			is a bookshelf and enough books to fill it.
		</p>
	</div>
	<div>
		<p class="font-bold">Seal Your Doors:</p>
		<p>
			If you live in an apartment with an adjoining hallway, you know the pains
			of people walking up and down that hallway at all hours of the day.
			Replacing your apartment door with something substantially thick, as well
			as caulking the edges of the frame, will cut that noise in half.
		</p>
	</div>
	<div>
		<p class="font-bold">Install Window Inserts:</p>
		<p>
			<a href="/resources/soundproofing-windows"
				>Un-insulated windows let in a ton of noise pollution</a
			>, almost more than any other part of your house. Window inserts are a
			boon for homeowners and renters alike who either can’t or don’t wish to
			make substantial renovations to their windows.
			<a href="/resources/blog/soundproof-apartment">
				They’re easy to install and brands like Indow offer up to a 70%
				reduction* in noise
			</a>.
		</p>
	</div>
	<p>
		Out of all the available <a
			href="/resources/blog/most-effective-soundproofing-materials"
			>soundproofing materials</a
		>, window inserts provide some of the best sound insulation money can buy.
		They’re inexpensive, easy to install, and offer more than just protection
		against noise pollution. <a href="/">Indow</a> is renowned throughout the industry
		not only for their window inserts, but the lengths they go to ensure that their
		inserts will fit your window frames and will provide the best protection possible.
	</p>
	<p>
		We’ve seen the effects of noise pollution on human health and now we know
		the methods and the means necessary to keep our homes and our ears safe from
		unwanted and damaging noise.
	</p>
	<p>
		If you’re confident that inserts are an affordable, efficient, and easy
		solution to the garbage trucks driving by in the morning or the lawnmowers
		in the afternoon, <a href="/how-to/get-indow-inserts"
			>head over to Indow and get an estimate today</a
		>
		to be one step closer to some peace and quiet. Or
		<a href="/resources/faq">check out our FAQ page</a> for more information on window
		inserts, their benefits, and more.
	</p>
	<p class="italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
</Section>

<Reviews />
